/**
 * Trash (L2): soft-delete pages. Restore = clear deleted_at
 * (normal write-path write; it also restores the cohort of subpages that fell
 * with it); "delete permanently" = physical deletion (irreversible, cascading
 * through subpages, blocks, attachments, and versions).
 */
import { createRoute } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { RotateCcw, Trash2 } from "@fleet/icons";
import { useTrashedPagesQuery } from "@folio/shared/solid";
import { deletePageMutation, restorePageFromTrashMutation, TRASH_PURGE_DAYS } from "@folio/shared";
import type { Page } from "@folio/powersync";
import { appRoute } from "./route";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { PageContainer, Skeleton } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { formatRelativeTime } from "@/features/notes/lib/format";

const DAY_MS = 86_400_000;

/**
 * Days until the server purge job permanently deletes a page
 * (TRASH_PURGE_DAYS from `deleted_at`). `null` if there is no valid
 * `deleted_at`; never negative (an expired purge is shown as 0,
 * "today" — the job runs hourly, so it soon disappears from the list).
 */
function daysUntilPurge(deletedAt: string | null): number | null {
  if (!deletedAt) {
    return null;
  }
  const deletedTime = new Date(deletedAt).getTime();
  if (Number.isNaN(deletedTime)) {
    return null;
  }
  const purgeTime = deletedTime + TRASH_PURGE_DAYS * DAY_MS;
  return Math.max(0, Math.ceil((purgeTime - Date.now()) / DAY_MS));
}

function TrashPage() {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();

  const deleted = useTrashedPagesQuery({
    db: () => db(),
    drizzle,
    userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });
  const [confirmPage, setConfirmPage] = createSignal<Page | null>(null);

  const restore = async (id: string) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await restorePageFromTrashMutation(dbInstance, id);
    } catch (err: unknown) {
      console.error("Error restoring page:", err instanceof Error ? err.message : String(err));
    }
  };

  const deleteForever = async (id: string) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await deletePageMutation(dbInstance, id);
    } catch (err: unknown) {
      console.error("Error deleting page:", err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <PageContainer maxWidth="2xl" class="flex flex-col gap-4 pb-16">
      <header class="flex flex-col gap-1 pt-2">
        <h1 class="flex items-center gap-2 text-xl font-bold text-foreground">
          <Trash2 class="h-5 w-5 text-muted-foreground" />
          {t(msg`Trash`)}
        </h1>
        <p class="text-xs text-muted-foreground">
          {t(msg`Deleted pages stay here until you restore or permanently delete them.`)}
        </p>
      </header>

      <Show
        when={!deleted.isLoading}
        fallback={
          <div class="flex flex-col gap-3">
            <Skeleton class="h-20 w-full rounded-2xl" />
            <Skeleton class="h-20 w-full rounded-2xl" />
          </div>
        }
      >
        <Show
          when={(deleted.data ?? []).length > 0}
          fallback={
            <div data-testid="trash-empty" class="flex flex-1">
              <EmptyState
                icon={Trash2}
                title={t(msg`Trash is empty`)}
                description={t(msg`Pages you delete will appear here so you can change your mind.`)}
              />
            </div>
          }
        >
          <section class="flex flex-col gap-3" data-testid="trash-list">
            <For each={deleted.data ?? []}>
              {(row) => (
                <article
                  class="flex flex-col gap-2 rounded-2xl border border-border/50 bg-surface/70 p-4 shadow-sm"
                  data-testid="trash-item"
                >
                  <div class="flex items-center justify-between gap-2">
                    <h2 class="flex items-center gap-2 truncate text-base font-semibold text-foreground">
                      <Show when={row.icon}>
                        <span>{row.icon}</span>
                      </Show>
                      <Show
                        when={row.title}
                        fallback={
                          <span class="italic text-muted-foreground">{t(msg`Untitled`)}</span>
                        }
                      >
                        {row.title}
                      </Show>
                    </h2>
                    <span class="shrink-0 text-xs text-muted-foreground/60">
                      {t(msg`Deleted {when}`, {
                        when: formatRelativeTime(row.deleted_at, locale()),
                      })}
                    </span>
                  </div>
                  <Show when={daysUntilPurge(row.deleted_at) !== null}>
                    <p class="text-xs text-muted-foreground/60" data-testid="trash-purge-countdown">
                      {t(msg`Will be permanently deleted in {days} days`, {
                        days: daysUntilPurge(row.deleted_at) ?? 0,
                      })}
                    </p>
                  </Show>
                  <div class="flex justify-end gap-2 pt-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      class="gap-1.5 text-muted-foreground hover:text-danger"
                      data-testid="trash-delete-forever"
                      onClick={() => setConfirmPage(row)}
                    >
                      <Trash2 class="h-4 w-4" />
                      {t(msg`Delete forever`)}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      class="gap-1.5"
                      data-testid="trash-restore"
                      onClick={() => {
                        void restore(row.id);
                      }}
                    >
                      <RotateCcw class="h-4 w-4" />
                      {t(msg`Restore`)}
                    </Button>
                  </div>
                </article>
              )}
            </For>
          </section>
        </Show>
      </Show>

      <ConfirmDialog
        title={t(msg`Delete forever?`)}
        description={t(
          msg`This page, its subpages, content and version history will be permanently deleted. This cannot be undone.`,
        )}
        confirmLabel={t(msg`Delete forever`)}
        cancelLabel={t(msg`Cancel`)}
        variant="destructive"
        open={confirmPage() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmPage(null);
          }
        }}
        onConfirm={() => {
          const row = confirmPage();
          if (row) {
            void deleteForever(row.id);
          }
          setConfirmPage(null);
        }}
      />
    </PageContainer>
  );
}

export const trashRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/trash",
  component: TrashPage,
});
